import { grantedDownloadTiers, type ShareDownloadTier } from '../../../src/sharePolicy/clientAccess'
import type { SharePolicyClientDto } from '../../../src/sharePolicy/contracts'

export type ClientDownloadAvailability = Partial<Readonly<Record<ShareDownloadTier, boolean>>>

export interface ClientDownloadMenuItem {
  tier: ShareDownloadTier
  label: string
  description: string
  href: string
}

const TIER_COPY: Readonly<
  Record<ShareDownloadTier, Pick<ClientDownloadMenuItem, 'label' | 'description'>>
> = Object.freeze({
  web: { label: 'Web-size', description: 'Prepared for quick sharing' },
  largest: { label: 'Largest', description: 'High-quality prepared copy' },
  jpg: { label: 'Original JPG', description: 'Camera-rendered file' },
  raw: { label: 'Original RAW', description: 'Camera-native file' },
})

/** Replace rather than duplicate the tier query while preserving other gates. */
export function withShareDownloadTier(url: string, tier: ShareDownloadTier): string {
  const base = typeof window === 'undefined' ? 'https://shotselect.invalid' : window.location.origin
  const parsed = new URL(url, base)
  parsed.searchParams.set('tier', tier)
  if (/^[A-Za-z][A-Za-z\d+.-]*:/.test(url)) return parsed.toString()
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

/**
 * Policy grants are the ceiling and per-photo object availability only narrows
 * them. A missing or ungranted tier never becomes a disabled/promised row.
 */
export function buildClientDownloadMenu(input: {
  policy: SharePolicyClientDto
  availability: ClientDownloadAvailability
  downloadUrl: string
}): readonly ClientDownloadMenuItem[] {
  return grantedDownloadTiers(input.policy)
    .filter((tier) => input.availability[tier])
    .map((tier) => ({
      tier,
      ...TIER_COPY[tier],
      href: withShareDownloadTier(input.downloadUrl, tier),
    }))
}
